/** Память вида по записи истории. Здесь нет React, DOM и ответов API. */
export interface ViewLocation {
  key: string
  pathname: string
  search?: string
}

export type ViewNavigation = 'POP' | 'PUSH' | 'REPLACE'
export interface ViewPosition { x: number; y: number }

interface ViewEntry {
  pathname: string
  values: Map<string, unknown>
  scroll: Map<string, ViewPosition>
  visibleScroll: Set<string>
  backgroundKey?: string
}

export const WINDOW_SCROLL = '$window'

/** Отдельный экземпляр на авторизованную сессию; старые записи ограничены по количеству. */
export class NavigationMemory {
  private entries = new Map<string, ViewEntry>()
  private trail: string[] = []
  private cursor = -1
  private active: ViewLocation | null = null
  private backRequestedFor: string | null = null
  private listeners = new Set<() => void>()
  readonly limit: number

  constructor(limit = 100) { this.limit = Math.max(2, limit) }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => { this.listeners.delete(listener) }
  }

  /** Подготовить независимый снимок до рендера детей, но историю менять только после commit. */
  prepare(location: ViewLocation, action: ViewNavigation, preserveScroll = false, backgroundKey?: string): void {
    if (this.entries.has(location.key)) return
    const previous = this.active && this.entries.get(this.active.key)
    const samePage = this.active?.pathname === location.pathname
    const inherit = action !== 'POP' && samePage && previous
    this.entries.set(location.key, {
      pathname: location.pathname,
      values: new Map(inherit ? previous.values : []),
      scroll: new Map(inherit && (action === 'REPLACE' || preserveScroll) ? previous.scroll : []),
      visibleScroll: new Set(inherit && (action === 'REPLACE' || preserveScroll) ? previous.visibleScroll : []),
      backgroundKey,
    })
    this.trim(location.key, false)
  }

  commit(location: ViewLocation, action: ViewNavigation): void {
    if (this.active?.key === location.key) return
    const previousKey = this.active?.key
    if (!this.entries.has(location.key)) this.prepare(location, action)
    if (this.cursor < 0) {
      this.trail = [location.key]
      this.cursor = 0
    } else if (action === 'PUSH') {
      this.trail = [...this.trail.slice(0, this.cursor + 1), location.key]
      this.cursor = this.trail.length - 1
    } else if (action === 'REPLACE') {
      this.trail[this.cursor] = location.key
    } else {
      const index = this.trail.indexOf(location.key)
      // Новая/восстановленная браузером запись не доказывает, что перед ней наш экран.
      if (index < 0) {
        this.trail = [location.key]
        this.cursor = 0
      } else this.cursor = index
    }
    this.active = { ...location }
    this.backRequestedFor = null
    if (this.trail.length > this.limit) {
      const excess = this.trail.length - this.limit
      this.trail.splice(0, excess)
      this.cursor = Math.max(0, this.cursor - excess)
    }
    // REPLACE не добавляет запись browser history. Отброшенные ключи не должны вытеснять родителя.
    if (action === 'REPLACE' && previousKey && !this.trail.includes(previousKey)
      && ![...this.entries.values()].some((entry) => entry.backgroundKey === previousKey)) this.entries.delete(previousKey)
    this.trim(location.key)
    this.listeners.forEach((listener) => listener())
  }

  canGoBack(key: string): boolean {
    return this.active?.key === key && this.cursor > 0
      && this.entries.has(this.trail[this.cursor - 1] ?? '')
  }

  takeBack(key: string): 'back' | 'fallback' | 'pending' {
    if (this.backRequestedFor === key) return 'pending'
    this.backRequestedFor = key
    return this.canGoBack(key) ? 'back' : 'fallback'
  }

  read<T>(key: string, slot: string, initial: T | (() => T)): T {
    const entry = this.entry(key)
    if (!entry.values.has(slot)) entry.values.set(slot, typeof initial === 'function' ? (initial as () => T)() : initial)
    return entry.values.get(slot) as T
  }

  write<T>(key: string, slot: string, value: T): void {
    const entry = this.entry(key)
    if (entry.values.has(slot) && Object.is(entry.values.get(slot), value)) return
    entry.values.set(slot, value)
    this.listeners.forEach((listener) => listener())
  }

  position(key: string, slot: string): ViewPosition | undefined {
    return this.entries.get(key)?.scroll.get(slot)
  }

  positions(key: string): ReadonlyMap<string, ViewPosition> {
    return this.entries.get(key)?.scroll ?? new Map()
  }

  savePosition(key: string, slot: string, value: ViewPosition): void {
    if (!Number.isFinite(value.x) || !Number.isFinite(value.y)) return
    this.entry(key).scroll.set(slot, { x: Math.max(0, value.x), y: Math.max(0, value.y) })
  }

  /** Явная команда экрана меняет выбранные области; Back/Forward всегда берут сохранённый вид. */
  resetPositions(key: string, action: ViewNavigation, slots: readonly string[]): void {
    if (action === 'POP') return
    const entry = this.entries.get(key)
    for (const slot of slots) entry?.scroll.delete(slot)
  }

  rememberVisibleScroll(key: string, slots: Iterable<string>): void {
    this.entry(key).visibleScroll = new Set(slots)
  }

  visibleScroll(key: string): ReadonlySet<string> {
    return this.entries.get(key)?.visibleScroll ?? new Set()
  }

  size(): number { return this.entries.size }

  private entry(key: string): ViewEntry {
    let entry = this.entries.get(key)
    if (!entry) {
      entry = { pathname: '', values: new Map(), scroll: new Map(), visibleScroll: new Set() }
      this.entries.set(key, entry)
      this.trim(key)
    }
    return entry
  }

  private trim(keep: string, evictHistory = true): void {
    const pinned = new Set([...this.entries.values()].map((entry) => entry.backgroundKey).filter((key): key is string => !!key))
    // Сначала убрать записи, уже заменённые/отсечённые в истории, или незавершённые рендеры.
    for (const key of this.entries.keys()) {
      if (this.entries.size <= this.limit) break
      if (key !== keep && key !== this.active?.key && !this.trail.includes(key) && !pinned.has(key)) this.entries.delete(key)
    }
    if (!evictHistory) return
    for (const key of this.entries.keys()) {
      if (this.entries.size <= this.limit) break
      if (key !== keep && key !== this.active?.key && !pinned.has(key)) this.entries.delete(key)
    }
  }
}

/** Родитель для прямой ссылки: никогда не уводит на внешнюю страницу из browser history. */
export function parentView(pathname: string): string {
  if (pathname.startsWith('/analytics/reels/')) return '/analytics'
  if (pathname === '/analytics') return '/tools'
  if (pathname.startsWith('/sources/')) return '/sources'
  if (pathname.startsWith('/favorites/')) return '/favorites'
  if (pathname.startsWith('/tools/')) return '/tools'
  return '/'
}
